import { useCallback } from 'react';

/**
 * Custom hook for handling agent actions from chat responses
 * Centralizes all agent action logic for reusability and maintainability
 */
const useAgentActions = ({
  showNavigation = true,
  portalMode = false,
  tutorialMode = false, // Prevent tutorial actions when already in tutorial
  currentContext = null,
  onNavigateToAnalysis,
  onLaunchLLMPortal,
  setChoiceCardsData,
  setIsChoiceCardsModalOpen,
  handleDirectTutorialCreation,
  handleTutorialStart,
  tourManager
}) => {
  
  const handleAgentActions = useCallback((responseData) => {
    if (!responseData.action) return;

    console.log(`🤖 [useAgentActions] Handling action: ${responseData.action}`, {
      action: responseData.action,
      showNavigation,
      portalMode,
      data: responseData.data
    });

    switch (responseData.action) {
      case 'navigate_to_analysis':
        if (showNavigation) {
          const { report_type, suggested_filters } = responseData.data;
          onNavigateToAnalysis('navigate_to_analysis', { 
            reportType: report_type, 
            filters: suggested_filters 
          });
        }
        break;

      case 'launch_llm_mode_test':
        {
          const { url, brand, keyword, secondary_keywords, scenario, persona, auto_run } = responseData.data;
          
          // Use URL from agent response, fallback to current context if not provided
          const targetUrl = url || currentContext?.page || currentContext?.url || window.location.pathname;
          
          // Check if we're already in portal mode - if so, reset with new params (always allow portal resets)
          if (portalMode) {
            // We're already in portal mode - reset portal with new analysis params
            const autoTestData = {
              url: targetUrl,
              brand: brand,
              primary_keyword: keyword,
              secondary_keywords: secondary_keywords,
              scenario: scenario,
              persona: persona || {},
              auto_run: auto_run !== false // Default to true
            };
            
            // Create agent context for the reset
            const triggerMessage = responseData.message || 
              `I'll test your AI citation likelihood for ${brand || 'your site'} and "${keyword}". Let me set up the new analysis.`;
            
            const agentContext = {
              lastMessage: triggerMessage,
              timestamp: new Date()
            };
            
            // Reset portal with new test data (portal resets work regardless of showNavigation)
            onLaunchLLMPortal({ 
              autoTestData,
              agentContext,
              resetPortal: true // Flag to indicate this is a reset, not a new portal
            });
          } else if (showNavigation) {
            // Not in portal mode - only open new portal if navigation is allowed
            // Prepare auto-test data for LLM Portal
            const autoTestData = {
              url: targetUrl,
              brand: brand,
              primary_keyword: keyword,
              secondary_keywords: secondary_keywords,
              scenario: scenario,
              persona: persona || {}, // Use agent-provided persona as-is, empty object if not provided
              auto_run: auto_run !== false // Default to true
            };
            
            // Create agent context from the actual agent response message (with fallback)
            const triggerMessage = responseData.message || 
              `I'll test your AI citation likelihood for ${brand || 'your site'} and "${keyword}". Let me set up the analysis with the details you provided.`;
            
            const agentContext = {
              lastMessage: triggerMessage,
              timestamp: new Date()
            };
            
            // Launch LLM Portal with auto-test data and agent context
            onLaunchLLMPortal({ 
              autoTestData,
              agentContext
            });
          }
        }
        break;

      case 'navigate_to_page':
        if (showNavigation) {
          const { page_url, tab, autorun, keyword } = responseData.data;
          console.log('🎯 [useAgentActions] navigate_to_page with autorun:', { page_url, tab, autorun, keyword });
          onNavigateToAnalysis('navigate_to_page', { 
            page_url: page_url, 
            tab: tab,
            autorun: autorun,
            keyword: keyword
          });
        }
        break;

      case 'trigger_analysis':
        if (showNavigation) {
          const { analysis_type, date_ranges, country } = responseData.data;
          onNavigateToAnalysis('trigger_analysis', { 
            analysis_type: analysis_type, 
            date_ranges: date_ranges,
            country: country
          });
        }
        break;

      case 'navigate_to_prompt_burst':
        if (showNavigation) {
          // Navigate to Prompt Burst analysis tool
          onNavigateToAnalysis('navigate_to_prompt_burst', {});
        }
        break;

      case 'auto_run_prompt_burst':
        // Auto-run Prompt Burst should work even from within other analysis tools
        // as it's meant to replace/update the current analysis
        onNavigateToAnalysis('auto_run_prompt_burst', responseData.data);
        break;

      case 'auto_run_ai_crawler':
        // Auto-run AI Crawler should work even from within other analysis tools
        // as it's meant to replace/update the current analysis
        onNavigateToAnalysis('auto_run_ai_crawler', responseData.data);
        break;

      case 'navigate_to_ai_crawler':
        if (showNavigation) {
          // Navigate to AI Crawler analysis tool
          onNavigateToAnalysis('navigate_to_ai_crawler', {});
        }
        break;

      case 'highlight_table_rows':
        {
          const { row_ids, explanation } = responseData.data;
          // Use TourManager to highlight the rows
          if (tourManager) {
            tourManager.highlightTableRows(row_ids, 15000); // 15 second highlight duration
          }
        }
        break;

      case 'present_choice_cards':
        {
          const { cards, preferred_choice, preference_reasoning } = responseData.data;
          if (setChoiceCardsData && setIsChoiceCardsModalOpen) {
            setChoiceCardsData(responseData.data);
            setIsChoiceCardsModalOpen(true);
          }
        }
        break;

      case 'present_tutorial_cards':
        {
          const { cards, context_summary, rationale } = responseData.data;
          if (setChoiceCardsData && setIsChoiceCardsModalOpen) {
            setChoiceCardsData(responseData.data);
            setIsChoiceCardsModalOpen(true);
          }
        }
        break;

      case 'create_tutorial':
        {
          // Don't trigger tutorial opening if already in tutorial mode
          if (tutorialMode) {
            console.log('🎓 [useAgentActions] Skipping tutorial creation - already in tutorial mode');
            break;
          }
          
          const { tutorial_title, tutorial_content, audit_findings, implementation_context } = responseData.data;
          const { rationale } = responseData;
          
          if (handleDirectTutorialCreation && handleTutorialStart) {
            // Transform the API response with all agent data
            const tutorialData = handleDirectTutorialCreation(tutorial_title, tutorial_content, {
              audit_findings,
              implementation_context,
              rationale
            });
            
            // Immediately launch the tutorial modal
            handleTutorialStart(tutorialData);
          }
        }
        break;

      default:
        console.warn(`🤖 [useAgentActions] Unhandled action: ${responseData.action}`);
        break;
    }
  }, [
    showNavigation,
    portalMode,
    tutorialMode,
    currentContext,
    onNavigateToAnalysis,
    onLaunchLLMPortal,
    setChoiceCardsData,
    setIsChoiceCardsModalOpen,
    handleDirectTutorialCreation,
    handleTutorialStart,
    tourManager
  ]);

  return {
    handleAgentActions
  };
};

export default useAgentActions;
